// ============================================================================
// ProjectileLayer - Renders flying projectiles
// ============================================================================
// Renders arrows and bullets as they fly across the screen

import { ProjectileType, type ProjectileTypeValue, TILE_SIZE } from '@dreamgame/shared';
import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { getAllProjectiles } from '../../state/projectiles';
import { loadTexture, onCategoryReload } from '../TextureManager';

// ============================================================================
// Constants
// ============================================================================

// Projectile visual sizes
const ARROW_WIDTH = 24;
const ARROW_HEIGHT = 6;
const BULLET_SIZE = 8;

// Colors for fallback rendering
const PROJECTILE_COLORS: Record<number, number> = {
  [ProjectileType.ARROW_WOOD]: 0x8b4513,
  [ProjectileType.ARROW_STONE]: 0x808080,
  [ProjectileType.ARROW_IRON]: 0x666666,
  [ProjectileType.ARROW_BRONZE]: 0xcd7f32,
  [ProjectileType.ARROW_STEEL]: 0x4682b4,
  [ProjectileType.ARROW_SILVER]: 0xc0c0c0,
  [ProjectileType.ARROW_MITHRIL]: 0x00bfff,
  [ProjectileType.ARROW_STARMETAL]: 0x8a2be2,
  [ProjectileType.LEAD_BALL]: 0x2f4f4f,
};

// Texture file names for each projectile type
const PROJECTILE_TEXTURES: Partial<Record<ProjectileTypeValue, string>> = {
  [ProjectileType.ARROW_WOOD]: 'arrow_wood.png',
  [ProjectileType.ARROW_STONE]: 'arrow_stone.png',
  [ProjectileType.ARROW_IRON]: 'arrow_iron.png',
  [ProjectileType.ARROW_BRONZE]: 'arrow_bronze.png',
  [ProjectileType.ARROW_STEEL]: 'arrow_steel.png',
  [ProjectileType.ARROW_SILVER]: 'arrow_silver.png',
  [ProjectileType.ARROW_MITHRIL]: 'arrow_mithril.png',
  [ProjectileType.ARROW_STARMETAL]: 'arrow_starmetal.png',
  [ProjectileType.LEAD_BALL]: 'lead_ball.png',
};

// ============================================================================
// State
// ============================================================================

let container: Container;
let graphics: Graphics;

const projectileTextures: Map<ProjectileTypeValue, Texture> = new Map();
const projectileSprites: Map<number, Sprite> = new Map();
const usedSpriteIds: Set<number> = new Set();

// ============================================================================
// Layer Creation
// ============================================================================

export const createProjectileLayer = (): { container: Container; graphics: Graphics } => {
  container = new Container();
  graphics = new Graphics();
  return { container, graphics };
};

// ============================================================================
// Texture Loading
// ============================================================================

export const loadProjectileTextures = async (): Promise<void> => {
  try {
    for (const [type, filename] of Object.entries(PROJECTILE_TEXTURES)) {
      const projectileType = Number(type) as ProjectileTypeValue;
      try {
        const texture = await loadTexture({ path: `/projectiles/${filename}` });
        projectileTextures.set(projectileType, texture);
      } catch {
        // Texture not found, will use fallback
      }
    }
    console.log(`Loaded ${projectileTextures.size} projectile textures`);
  } catch (error) {
    console.warn('Failed to load projectile textures, using fallback colors', error);
  }
};

export const reloadProjectileTextures = async (): Promise<void> => {
  // Clear existing sprites
  for (const sprite of projectileSprites.values()) {
    sprite.destroy();
  }
  projectileSprites.clear();

  // Clear texture cache
  projectileTextures.clear();

  // Reload textures
  await loadProjectileTextures();
  console.log('Projectile textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'projectiles', callback: reloadProjectileTextures });

// ============================================================================
// Drawing
// ============================================================================

// Update or create sprite for projectile
const renderProjectileSprite = ({
  networkId,
  worldX,
  worldY,
  rotation,
  projectileType,
  texture,
}: {
  networkId: number;
  worldX: number;
  worldY: number;
  rotation: number;
  projectileType: ProjectileTypeValue;
  texture: Texture;
}): void => {
  const existingSprite = projectileSprites.get(networkId);

  if (existingSprite) {
    existingSprite.x = worldX;
    existingSprite.y = worldY;
    existingSprite.rotation = rotation;
    if (existingSprite.texture !== texture) {
      existingSprite.texture = texture;
    }
  } else {
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5, 0.5);
    sprite.x = worldX;
    sprite.y = worldY;
    sprite.rotation = rotation;
    sprite.width = projectileType === ProjectileType.LEAD_BALL ? BULLET_SIZE : ARROW_WIDTH;
    sprite.height = projectileType === ProjectileType.LEAD_BALL ? BULLET_SIZE : ARROW_HEIGHT;
    container.addChild(sprite);
    projectileSprites.set(networkId, sprite);
  }
};

// Draw arrow using graphics fallback
const drawArrowFallback = ({
  worldX,
  worldY,
  rotation,
  color,
}: {
  worldX: number;
  worldY: number;
  rotation: number;
  color: number;
}): void => {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const halfW = ARROW_WIDTH / 2;
  const halfH = ARROW_HEIGHT / 2;

  // Arrow body
  const corners = [
    { x: -halfW, y: -halfH },
    { x: halfW, y: -halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: halfH },
  ].map(({ x, y }) => ({
    x: worldX + x * cos - y * sin,
    y: worldY + x * sin + y * cos,
  }));

  graphics.poly(corners);
  graphics.fill({ color });

  // Arrow head
  const headPoints = [
    { x: halfW, y: 0 },
    { x: halfW - 6, y: -4 },
    { x: halfW - 6, y: 4 },
  ].map(({ x, y }) => ({
    x: worldX + x * cos - y * sin,
    y: worldY + x * sin + y * cos,
  }));

  graphics.poly(headPoints);
  graphics.fill({ color: 0x444444 });
};

// Draw bullet using graphics fallback
const drawBulletFallback = ({
  worldX,
  worldY,
  color,
}: {
  worldX: number;
  worldY: number;
  color: number;
}): void => {
  graphics.circle(worldX, worldY, BULLET_SIZE / 2);
  graphics.fill({ color });
};

// Clean up sprites for despawned projectiles
const cleanupUnusedSprites = (): void => {
  for (const [id, sprite] of projectileSprites) {
    if (!usedSpriteIds.has(id)) {
      sprite.destroy();
      projectileSprites.delete(id);
    }
  }
};

export const drawProjectiles = (): void => {
  graphics.clear();
  usedSpriteIds.clear();

  const projectiles = getAllProjectiles();

  for (const projectile of projectiles) {
    const worldX = projectile.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = projectile.y * TILE_SIZE + TILE_SIZE / 2;
    const projectileType = projectile.projectileType as ProjectileTypeValue;
    const rotation = Math.atan2(
      projectile.targetY - projectile.y,
      projectile.targetX - projectile.x
    );

    usedSpriteIds.add(projectile.networkId);

    const texture = projectileTextures.get(projectileType);
    if (texture) {
      renderProjectileSprite({
        networkId: projectile.networkId,
        worldX,
        worldY,
        rotation,
        projectileType,
        texture,
      });
    } else {
      const color = PROJECTILE_COLORS[projectileType] ?? 0x888888;
      if (projectileType === ProjectileType.LEAD_BALL) {
        drawBulletFallback({ worldX, worldY, color });
      } else {
        drawArrowFallback({ worldX, worldY, rotation, color });
      }
    }
  }

  cleanupUnusedSprites();
};
